import React, { FC, useCallback, useState, useEffect } from 'react'
import { useHistory } from 'react-router-dom'
import { FormProvider, useForm } from 'react-hook-form'
import { toast } from 'react-toastify'
import { makeStyles } from '@mui/styles'
import { Divider, Grid, Typography, FormHelperText, InputLabel } from '@mui/material'
import { Theme } from '@mui/material/styles'
import { classValidatorResolver } from '@hookform/resolvers/class-validator'
import { CreateCompanyRequestDto } from '@sparling/financial-common'
import { useSelector } from 'react-redux'
import CountrySelect from '../../../components/CountrySelect'
import { InputField } from '../../../components/InputField'
import { GreenButton } from '../../../components/Buttons/GreenButton'
import { TransparentButton } from '../../../components/Buttons/TransparentButton'
import { routes } from '../../../constants/routes'
import { useRest } from '../../../components/Context'
import useDebounce from '../../../hooks/useDebounce'
import { Colors } from '../../../theme'
import { InputPhone } from '../../../components/InputPhone'
import { InputSelect } from '../../../components/InputSelect'
import { DataPicker } from '../../../components/DatePicker'
import { fiatsEnumSelector } from '../../../store/selectors'

const useStyles = makeStyles((theme: Theme) => ({
  topWrap: {
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingTop: theme.spacing(6),
  },
  title: {
    lineHeight: 0,
  },
  buttonWrap: {
    justifyContent: 'space-between',
    width: 256,
  },
  formContent: {
    flexDirection: 'column',
    justifyContent: 'space-evenly',
    width: '100%',
    margin: '32px 32px 32px 0',
    padding: 32,
    background: 'rgba(216, 216, 216, 0.05)',
  },
  inputWrap: {
    justifyContent: 'space-between',
    alignItems: 'stretch',
    width: 632,
    flexWrap: 'wrap',
  },
  horizontalLine: {
    margin: '10px 0 30px 0',
    backgroundColor: '#E9ECEF',
    opacity: '0.38',
  },
  companyNameError: {
    color: Colors.yellow,
    marginTop: -17,
    width: 300,
  },
  dateWrap: {
    maxHeight: 63,
  },
  dateInput: {
    fontSize: '14px',
    // marginTop: 3,
    color: '#FFFFFF',
    opacity: '60%',
  },
}))

const resolver = classValidatorResolver(CreateCompanyRequestDto)

type FormInputs = {
  name: string
  email: string
  phone: string
  currency: string
  personalContact: string
  country: string
  city: string
  postCode: string
}

export const CreateCompany: FC = () => {
  const classes = useStyles()
  const { rxios } = useRest()
  const history = useHistory()
  const fiatsEnum = useSelector(fiatsEnumSelector)
  const [nameCompany, setNameCompany] = useState<string | undefined>()
  const [nameCompanyError, setNameCompanyError] = useState<string | boolean>(false)
  const debouncedValue = useDebounce<string | undefined>(nameCompany, 200)
  const [companyEmailError, setCompanyEmailError] = useState<string>()
  const [companyEmail, setCompanyEmail] = useState<string | undefined>()
  const debouncedEmailValue = useDebounce<string | undefined>(companyEmail, 200)
  const [companyPhoneError, setCompanyPhoneError] = useState<string | boolean>(false)
  const [companyPhone, setCompanyPhone] = useState<string | undefined>()
  const debouncedPhoneValue = useDebounce<string | undefined>(companyPhone, 200)

  const formMethods = useForm<FormInputs>({
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    shouldFocusError: true,
    resolver,
  })
  const { handleSubmit, watch, formState } = formMethods

  const nameVerification = useCallback(
    (name: string) => {
      rxios
        .post('company/unique-name', {
          name,
        })
        .subscribe(
          // eslint-disable-next-line
          (next: any) => {
            if (next.success) {
              setNameCompanyError(false)
            } else {
              setNameCompanyError(next.message)
            }
          },
          (error) => {
            toast.error(error.response.data.message)
          },
        )
      // eslint-disable-next-line
    },
    [rxios],
  )

  const phoneVerification = useCallback(
    (phone: string) => {
      rxios.post('company/unique-phone', { phone }).subscribe(
        // eslint-disable-next-line
        (next: any) => {
          if (next.success) {
            setCompanyPhoneError(false)
          } else {
            setCompanyPhoneError(next.message)
          }
        },
        (error) => {
          toast.error(error.response.data.message)
        },
      )
      // eslint-disable-next-line
    },
    [rxios],
  )

  const emailVerification = useCallback(
    (email: string) => {
      rxios.post('company/unique-email', { email }).subscribe(
        // eslint-disable-next-line
        (next: any) => {
          if (next.success) {
            setCompanyEmailError('')
          } else {
            setCompanyEmailError(next.message)
          }
        },
        (error) => {
          toast.error(error.response.data.message)
        },
      )
      // eslint-disable-next-line
    },
    [rxios],
  )

  useEffect(() => {
    const subscription = watch((value, { name }) => {
      if (name === 'name') setNameCompany(value[name])
      if (name === 'phone') setCompanyPhone(value[name])
      if (name === 'email') setCompanyEmail(value[name])
    })
    return () => subscription.unsubscribe()
  }, [watch])

  useEffect(() => {
    if (formState.errors.name) {
      setNameCompanyError(false)
    }
    if (nameCompany) nameVerification(nameCompany)
    // eslint-disable-next-line
  }, [debouncedValue])

  useEffect(() => {
    if (formState.errors.phone) {
      setCompanyPhoneError(false)
    }
    if (companyPhone) phoneVerification(companyPhone)
    // eslint-disable-next-line
  }, [debouncedPhoneValue])

  useEffect(() => {
    if (formState.errors.email) {
      setCompanyEmailError('')
    }
    if (companyEmail) emailVerification(companyEmail)
    // eslint-disable-next-line
  }, [debouncedEmailValue])

  const onSubmit = useCallback(
    // eslint-disable-next-line
    async (data: any) => {
      const postData = data
      postData.introducedDate = data.introducedDate || null
      rxios.post('company', postData).subscribe(
        // eslint-disable-next-line
        (next: any) => {},
        // eslint-disable-next-line
        (error: any) => {
          const text = error.response.data.message?.match(/\(([^)]+)\)/g)
          if (text) {
            const message = text[1].replace(/[()]/g, '')
            if (/\(email\)/.test(error.response.data.message)) {
              toast.error(`Email '${message}' is already registered. Please use another email.`)
            } else {
              toast.error(error.response.data.message)
            }
          } else {
            toast.error(error.response.data.message)
          }
        },
        () => {
          toast.success(`Company ${data.name} was created`)
          history.push(routes.COMPANIES)
        },
      )
    },
    [history, rxios],
  )

  return (
    <FormProvider {...formMethods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid container className={classes.topWrap}>
          <Typography variant="h1" className={classes.title}>
            Create Company
          </Typography>
          <Grid container item className={classes.buttonWrap}>
            <TransparentButton width={120} onClick={history.goBack} text="Cancel" />
            <GreenButton type="submit" width={120} text="Save" />
          </Grid>
        </Grid>
        <Grid item container className={classes.formContent}>
          <Grid item container className={classes.inputWrap}>
            <Grid>
              <InputField inputName={'name'} labelName={'Company Legal Name'} inputType={'text'} />
              {!formState.errors.name && nameCompanyError && (
                <FormHelperText className={classes.companyNameError}>{nameCompanyError}</FormHelperText>
              )}
            </Grid>
            <InputField inputName={'dbaName'} labelName={'Company DBA Name'} inputType={'text'} />

            <InputField
              inputName={'email'}
              labelName={'Email'}
              inputType={'text'}
              errorMessageText={companyEmailError}
              maximumHeight={'auto'}
            />
            <InputPhone phoneError={companyPhoneError} />
            <InputField inputName={'settlementContact'} labelName={'Settlement Contact Info'} inputType={'text'} />
            <InputField inputName={'complianceContact'} labelName={'Compliance Contact Info'} inputType={'text'} />
            <InputField inputName={'personalContact'} labelName={'Main Point of Contact'} inputType={'text'} />
            <InputSelect
              inputName={'currency'}
              labelName={'Currency'}
              width={300}
              placeholder={'Please Select'}
              enums={fiatsEnum}
            />
            <InputField inputName={'introducedBy'} labelName={'Introduced By'} inputType={'text'} />
            <Typography component={'div'} className={classes.dateWrap}>
              <InputLabel className={classes.dateInput}>Introduced Date</InputLabel>
              <DataPicker width={300} margin={0} mt={3} dataPickerName={'introducedDate'} required={true} />
            </Typography>
          </Grid>

          <Divider className={classes.horizontalLine} variant={'fullWidth'} />

          <Grid item container className={classes.inputWrap}>
            <CountrySelect
              inputName={'country'}
              labelName={'Country'}
              placeholder={'Please Select'}
              width={300}
              mb={25}
            />
            <InputField inputName={'city'} labelName={'City'} inputType={'text'} />
            <InputField inputName={'address'} labelName={'Address'} inputType={'text'} />
            <InputField inputName={'postCode'} labelName={'Post Code'} inputType={'text'} />
          </Grid>
        </Grid>
      </form>
    </FormProvider>
  )
}
